// PixCheckout — overlay fullscreen com QR PIX + copia-e-cola + timer + auto-poll.
// Branded com paleta Forjamente (midnight + ember). Usuário paga via app
// do banco dele e o backend webhook ativa o plano automático.
//
// Props:
//   payload  — resposta de /api/checkout/start-mp-pix
//   session  — pra polling de status
//   onPaid   — callback quando webhook confirma pagamento (close + toast + reload)
//   onClose  — usuário cancelou/voltou (pode ter pagado depois — webhook ainda
//              vai chegar e ativar o plano via DB)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './icons';
import { toast } from './toast';
import { api } from '../services/apiV2';
import { AuthSession } from '../types';

type PixPayload = {
  payment_id: string;
  amount: number;
  qr_code: string;
  qr_code_base64: string;
  ticket_url: string;
  expires_at: string;
  plan_id: string;
  plan_label: string;
};

const POLL_INTERVAL_MS = 5000;

function formatBRL(value: number): string {
  try {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  } catch {
    return `R$ ${value.toFixed(2).replace('.', ',')}`;
  }
}

function formatTimeLeft(ms: number): string {
  if (ms <= 0) return '00:00';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60).toString().padStart(2, '0');
  const s = (total % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export const PixCheckout: React.FC<{
  payload: PixPayload;
  session: AuthSession | null;
  onPaid: () => void;
  onClose: () => void;
}> = ({ payload, session, onPaid, onClose }) => {
  const expiresAt = useMemo(() => new Date(payload.expires_at).getTime(), [payload.expires_at]);
  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState(false);
  const [polling, setPolling] = useState(true);
  const [paid, setPaid] = useState(false);
  const pollRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);

  const msLeft = expiresAt - now;
  const expired = msLeft <= 0;

  // Tick do timer (1s).
  useEffect(() => {
    tickRef.current = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
    };
  }, []);

  // Polling de status (5s) até pagar ou expirar.
  useEffect(() => {
    if (paid || expired) {
      setPolling(false);
      return;
    }
    let cancelled = false;
    pollRef.current = window.setInterval(async () => {
      if (cancelled) return;
      try {
        const r = await api.getPixStatus(session, payload.payment_id);
        if (cancelled) return;
        // PAGO E LIBERADO. "Pago" sozinho não basta: quem libera o plano é o
        // servidor (o webhook ou esta mesma conferência), e fechar a tela com
        // o plano ainda por ativar devolvia a pessoa ao paywall depois de pagar.
        // Enquanto não estiver ativo, continua conferindo — e cada conferência
        // tenta ativar de novo.
        if (r.paid && r.plano_ativo) {
          setPaid(true);
          setPolling(false);
          if (pollRef.current) window.clearInterval(pollRef.current);
          // Pequeno delay pra UX (animação de check) antes de fechar.
          setTimeout(() => onPaid(), 1200);
        } else if (r.status === 'rejected' || r.status === 'cancelled') {
          // PIX falhou (reject improvável, cancel user no banco).
          if (pollRef.current) window.clearInterval(pollRef.current);
          setPolling(false);
        }
      } catch (e) {
        // Silent fail — só polling. Tenta de novo no próximo tick.
      }
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [paid, expired, session, payload.payment_id, onPaid]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(payload.qr_code);
      setCopied(true);
      toast.success('Código copiado.');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback: prompt manual.
      try {
        const ta = document.createElement('textarea');
        ta.value = payload.qr_code;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        toast.success('Código copiado.');
        setTimeout(() => setCopied(false), 2500);
      } catch {
        toast.error('Não consegui copiar — selecione e copie manualmente.');
      }
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 900,
      background: 'var(--paper)', overflowY: 'auto',
    }}>
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '40px 20px 60px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <button onClick={onClose} style={{
            width: 38, height: 38, borderRadius: '50%',
            background: 'var(--paper-2)', border: '1px solid var(--line)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="chevron-left" size={18}/>
          </button>
          <span className="mono" style={{
            fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ember)',
          }}>
            Pagamento Pix
          </span>
          <div style={{ width: 38 }}/>
        </div>

        {paid ? (
          <PaidState planLabel={payload.plan_label} onClose={onPaid}/>
        ) : expired ? (
          <ExpiredState onRetry={onClose}/>
        ) : (
          <>
            {/* Resumo */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div className="serif" style={{ fontSize: 28, lineHeight: 1.1 }}>
                Forjamente <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>{payload.plan_label}</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 6 }}>
                12 meses · pagamento único via Pix
              </div>
              <div className="serif" style={{
                fontSize: 44, lineHeight: 1, marginTop: 18,
                letterSpacing: '-0.02em', color: 'var(--ink)',
              }}>
                {formatBRL(payload.amount)}
              </div>
            </div>

            {/* QR code */}
            <div style={{
              background: '#FFFFFF',
              padding: 18, borderRadius: 22,
              border: '1px solid var(--line)',
              display: 'flex', justifyContent: 'center',
              marginBottom: 18,
            }}>
              <img
                alt="QR Code Pix"
                src={`data:image/png;base64,${payload.qr_code_base64}`}
                style={{ width: '100%', maxWidth: 280, height: 'auto', display: 'block' }}
              />
            </div>

            {/* Timer */}
            <div style={{
              textAlign: 'center', fontSize: 13, color: msLeft < 5 * 60 * 1000 ? 'var(--ember)' : 'var(--ink-3)',
              marginBottom: 18, fontFamily: 'Geist Mono, monospace',
            }}>
              ⏱ Expira em <strong>{formatTimeLeft(msLeft)}</strong>
            </div>

            {/* Copia e cola */}
            <div className="card" style={{ padding: 14, marginBottom: 14 }}>
              <div className="mono" style={{
                fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase',
                color: 'var(--ink-3)', marginBottom: 8,
              }}>
                Pix Copia e Cola
              </div>
              <div style={{
                fontSize: 11, color: 'var(--ink-2)',
                fontFamily: 'Geist Mono, monospace',
                wordBreak: 'break-all', lineHeight: 1.5,
                background: 'var(--paper-2)',
                padding: 10, borderRadius: 10,
                maxHeight: 80, overflowY: 'auto',
                marginBottom: 10,
              }}>
                {payload.qr_code}
              </div>
              <button onClick={handleCopy} style={{
                width: '100%', padding: '14px 16px', borderRadius: 14,
                background: copied ? 'var(--sage)' : 'var(--ember)',
                color: 'var(--paper)', fontSize: 14, fontWeight: 500,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
                <Icon name={copied ? 'check' : 'share'} size={16} color="var(--paper)"/>
                {copied ? 'Copiado' : 'Copiar código Pix'}
              </button>
            </div>

            {/* Instruções */}
            <div className="card" style={{ padding: 16, marginBottom: 14 }}>
              <div className="serif" style={{ fontSize: 17, lineHeight: 1.2, marginBottom: 8 }}>
                Como pagar
              </div>
              <ol style={{
                fontSize: 13, color: 'var(--ink-2)',
                paddingLeft: 22, lineHeight: 1.7, margin: 0,
              }}>
                <li>Abra o app do seu banco</li>
                <li>Vá em <strong>Pix</strong> → <strong>Pagar com QR Code</strong> (ou <strong>Copia e Cola</strong>)</li>
                <li>Aponte a câmera para o QR ou cole o código copiado</li>
                <li>Confirme o pagamento de <strong>{formatBRL(payload.amount)}</strong></li>
              </ol>
            </div>

            {/* Status polling */}
            <div style={{
              textAlign: 'center', fontSize: 12, color: 'var(--ink-3)',
              padding: '10px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              {polling ? (
                <>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: 'var(--ember)',
                    animation: 'pulse 1.4s ease-in-out infinite',
                  }}/>
                  Aguardando pagamento... assim que cair, ativamos seu plano.
                </>
              ) : (
                <span>Polling pausado.</span>
              )}
            </div>

            <style>{`
              @keyframes pulse {
                0%, 100% { opacity: 1; transform: scale(1) }
                50% { opacity: 0.4; transform: scale(0.7) }
              }
            `}</style>
          </>
        )}
      </div>
    </div>
  );
};

function PaidState({ planLabel, onClose }: { planLabel: string; onClose: () => void }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <div style={{
        width: 96, height: 96, borderRadius: '50%',
        background: 'var(--sage-soft, #d7eadc)', color: 'var(--sage, #3a6a48)',
        margin: '0 auto 22px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 48,
      }}>✓</div>
      <div className="serif" style={{ fontSize: 32, lineHeight: 1.1 }}>
        Pagamento <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>aprovado</span>.
      </div>
      <div style={{ fontSize: 14, color: 'var(--ink-2)', marginTop: 12, lineHeight: 1.5 }}>
        Seu plano <strong>{planLabel}</strong> está ativo por 12 meses.<br/>
        Vamos te lembrar antes de vencer pra você renovar.
      </div>
      <button onClick={onClose} style={{
        marginTop: 28, padding: '14px 28px', borderRadius: 14,
        background: 'var(--ember)', color: 'var(--paper)',
        fontSize: 14, fontWeight: 500,
      }}>Continuar</button>
    </div>
  );
}

function ExpiredState({ onRetry }: { onRetry: () => void }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <div style={{
        width: 84, height: 84, borderRadius: '50%',
        background: 'rgba(192,58,42,0.08)', color: '#C03A2A',
        margin: '0 auto 22px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 36,
      }}>⌛</div>
      <div className="serif" style={{ fontSize: 26, lineHeight: 1.1 }}>
        QR Code <span style={{ fontStyle: 'italic', color: '#C03A2A' }}>expirou</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 12 }}>
        Sem problema. Volte e gere um novo Pix.
      </div>
      <button onClick={onRetry} style={{
        marginTop: 22, padding: '14px 28px', borderRadius: 14,
        background: 'var(--ember)', color: 'var(--paper)', fontSize: 14, fontWeight: 500,
      }}>Tentar novamente</button>
    </div>
  );
}
